import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output } from "@angular/core";
import { GridsterConfig, GridsterItem } from "angular-gridster2";
import { Subject } from "rxjs";
import { Dialog } from "@core/material";
import { ReportImageEditFormDialog, ReportImageEditFormDialogData } from "../../report-dialogs/report-image-edit-form/report-image-edit-form.dialog";
import { FormControl, FormGroup } from "@angular/forms";
import { editModeCheck } from "../../report-view/report-state/advanced-report.state";
import { ReportComponentType } from '@core/data/types/advanced-report';

@Component({
    selector: 'pv-report-image',
    changeDetection: ChangeDetectionStrategy.Default,
    preserveWhitespaces: false,
    templateUrl: 'report-image-component.html',
    styleUrls: ['report-image-component.scss'],
})
export class ReportImageComponent implements OnChanges, OnDestroy {

    @Input()
    item: GridsterItem;

    @Input()
    editMode: boolean

    @Input()
    imageControl: FormControl;

    @Input()
    configFormGroup: FormGroup;

    @Input()
    reportKey: string;

    @Input()
    ownerOrgKey: string;

    @Input()
    gridsterConfig: GridsterConfig;

    @Output()
    delete = new EventEmitter<boolean>();

    @Output()
    duplicate = new EventEmitter<ReportComponentType>();

    layerIndex = 1;

    /** Ephemeral edit-mode toggle — not persisted; a reload always starts out of reposition mode. */
    repositioning = false;

    /** Unset until an image is chosen — drives the placeholder fallback instead of pv-image-ref's own empty state. */
    get isUnset(): boolean {
        return !this.imageControl?.value?.fileRef;
    }

    private _destroy$ = new Subject();

    private _dragging = false;
    private _dragStartX = 0;
    private _dragStartY = 0;
    private _dragStartFocalX = 0.5;
    private _dragStartFocalY = 0.5;
    private _justFinishedDrag = false;

    // The image's true natural pixel dimensions — required to compute a zoom-aware crop. Populated
    // once pv-image-ref's underlying <img> finishes loading (see onNaturalSize).
    private _naturalWidth: number = null;
    private _naturalHeight: number = null;

    constructor(
        private _dialog: Dialog,
        private _el: ElementRef,
    ) {}

    ngOnChanges() {
        this.layerIndex = this.item?.layerIndex ? this.item.layerIndex : 1;

        // Deferred so this runs after the current render/layout pass — e.g. on a frame resize, reading
        // the frame's clientWidth/clientHeight synchronously here could still see the pre-resize box.
        setTimeout(() => this._applyCropStyle());
    }

    onNaturalSize(size: { width: number, height: number }) {
        this._naturalWidth = size.width;
        this._naturalHeight = size.height;
        this._applyCropStyle();
    }

    private _getFrameEl(): HTMLElement | null {
        return this._el.nativeElement.querySelector('.content');
    }

    // The z-index gridster-item had before reposition mode overrode it — restored on exit rather than
    // just cleared, since angular-gridster2 assigns this inline value itself (from the component's
    // layerIndex) and clearing it outright would drop that ordering instead of restoring it.
    private _originalGridsterItemZIndex: string = null;

    // angular-gridster2 sets 'overflow: hidden' on its own .gridster-item wrapper (an ancestor of this
    // component's whole view, added by the library's own stylesheet, not inline) — this clips the
    // reposition-mode image spillover (the full, uncropped image showing beyond the frame for context)
    // regardless of this component's own overflow:visible further down, since an ancestor's
    // overflow:hidden clips descendants no matter their own overflow value. Toggled imperatively
    // (inline style always wins over the library's class rule) rather than via a template class, since
    // gridster-item lives outside this component's own encapsulated view.
    //
    // Also lifts the item's z-index while repositioning: the page's margin guide overlay
    // (report-page.view.html's .page-margin-guide) shares gridster-items' own z-index tier and is
    // declared later in the template, so at equal z-index it would otherwise paint on top of the
    // spilled-over image and the crop boundary ring. Restored to the library's own value on exit so
    // normal per-component layering (Move Forward/Send to Back etc.) resumes unaffected.
    //
    // Also disables this item's resize handles while repositioning: they're separate DOM elements that
    // angular-gridster2 attaches its own drag listeners to directly, so they're never intercepted by
    // suppressGridsterDrag (which only catches events that bubble through the image itself) — without
    // this, grabbing a handle at the frame's edge could resize the whole component instead of panning
    // the focal point. Hidden outright (display:none) rather than just visually disabled, since a
    // hidden element can't receive the pointer events that would start a resize.
    private _setGridsterItemOverflow(visible: boolean) {
        // <gridster-item> is a custom element TAG, not a CSS class — its className is just whatever
        // Angular structural-directive markers happen to be present (e.g. "ng-star-inserted"), so this
        // must match on tagName rather than classList.
        let node: HTMLElement | null = this._el.nativeElement;
        while (node && node.tagName?.toLowerCase() !== 'gridster-item') {
            node = node.parentElement;
        }
        if (!node) return;

        if (visible) {
            this._originalGridsterItemZIndex = node.style.zIndex;
            node.style.overflow = 'visible';
            node.style.zIndex = '100000';
        } else {
            node.style.overflow = '';
            node.style.zIndex = this._originalGridsterItemZIndex || '';
            this._originalGridsterItemZIndex = null;
        }

        const handles = node.querySelectorAll('.gridster-item-resizable-handler');
        handles.forEach((handle) => {
            (handle as HTMLElement).style.display = visible ? 'none' : '';
        });

        // <gridster> itself — the whole report PAGE — ALSO has its own 'overflow: hidden' (see
        // _advanced-report.theme.scss's `gridster { ... }` rule, there to clip content exactly to the
        // printable page size). That clips the image spillover just as much as gridster-item's own
        // overflow did — this must be overridden too, or the spillover never gets past a sliver near
        // the frame. Deliberately NOT overridden further up than this: spillover should stay within the
        // report PAGE, not bleed into the surrounding app chrome (sidebar, toolbar), so clipping at
        // <gridster>'s own edge is the correct, intentional boundary — only its overflow needs freeing.
        let pageNode: HTMLElement | null = node.parentElement;
        while (pageNode && pageNode.tagName?.toLowerCase() !== 'gridster') {
            pageNode = pageNode.parentElement;
        }
        if (pageNode) {
            pageNode.style.overflow = visible ? 'visible' : '';
        }
    }

    // Computes the manual cover+zoom crop against the image's REAL natural size. This replaces relying
    // on CSS object-fit: cover + a separate transform: scale — that combination cannot produce vertical
    // (or horizontal) pan range in whichever axis the image's native aspect ratio already exactly fits
    // the frame at zoom 1, because object-position's pannable range is computed by the browser BEFORE
    // any later CSS transform is applied, using only the unscaled cover-fit overflow. Confirmed live: an
    // image wider (relatively) than its frame has zero vertical overflow under object-fit, so no zoom
    // level via transform: scale ever created vertical pan range — this computes the true cover+zoom
    // size directly instead, so overflow (and therefore pan range) exists in both axes once zoomed.
    private _computeCrop(): { renderedW: number, renderedH: number, overflowX: number, overflowY: number, frameW: number, frameH: number } | null {
        const frameEl = this._getFrameEl();
        if (!frameEl || !this._naturalWidth || !this._naturalHeight) return null;

        const frameW = frameEl.clientWidth;
        const frameH = frameEl.clientHeight;
        if (!frameW || !frameH) return null;

        const zoom = typeof this.configFormGroup.value.zoom === 'number' ? this.configFormGroup.value.zoom : 1;

        // Same formula as CSS object-fit: cover (scale to the larger of the two ratios so both
        // dimensions are >= the frame), then zoom multiplies that base scale further.
        const baseScale = Math.max(frameW / this._naturalWidth, frameH / this._naturalHeight);
        const scale = baseScale * zoom;

        const renderedW = this._naturalWidth * scale;
        const renderedH = this._naturalHeight * scale;

        return {
            renderedW,
            renderedH,
            overflowX: Math.max(0, renderedW - frameW),
            overflowY: Math.max(0, renderedH - frameH),
            frameW,
            frameH,
        };
    }

    private _applyCropStyle() {
        const frameEl = this._getFrameEl();
        const crop = this._computeCrop();
        if (!frameEl || !crop) return;

        const focalX = typeof this.configFormGroup.value.focalX === 'number' ? this.configFormGroup.value.focalX : 0.5;
        const focalY = typeof this.configFormGroup.value.focalY === 'number' ? this.configFormGroup.value.focalY : 0.5;

        const left = -crop.overflowX * focalX;
        const top = -crop.overflowY * focalY;

        // Set on the component's HOST element, not frameEl directly — CSS custom properties inherit
        // down through the DOM tree, and while repositioning there are TWO .content elements sharing
        // this same crop (the crisp real image plus the dimmed .ghost-context copy behind it); setting
        // here lets both pick up identical --crop-* values without duplicating this calculation.
        const hostEl = this._el.nativeElement as HTMLElement;
        hostEl.style.setProperty('--crop-width', `${crop.renderedW}px`);
        hostEl.style.setProperty('--crop-height', `${crop.renderedH}px`);
        hostEl.style.setProperty('--crop-left', `${left}px`);
        hostEl.style.setProperty('--crop-top', `${top}px`);
    }

    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }

    moveToTop() {
        if (!editModeCheck(this.editMode)) return;

        this.configFormGroup.patchValue({
            layerIndex: this.gridsterConfig.maxLayerIndex,
        });
    }

    moveForward() {
        if (!editModeCheck(this.editMode)) return;

        this.configFormGroup.patchValue({
            layerIndex: this.gridsterConfig.baseLayerIndex,
        });
    }

    moveDown() {
        if (!editModeCheck(this.editMode)) return;
        this.configFormGroup.patchValue({
            layerIndex: -this.gridsterConfig.baseLayerIndex,
        });
    }

    moveToBottom() {
        if (!editModeCheck(this.editMode)) return;

        this.configFormGroup.patchValue({
            layerIndex: -this.gridsterConfig.maxLayerIndex,
        });
    }

    roundImage() {
        if (!editModeCheck(this.editMode)) return;

        const form = this.configFormGroup.value;
        let radius = form.radius;

        if (radius == 0) radius = 50;
        else radius = 0;

        this.configFormGroup.patchValue({
            radius
        });
    }

    fillArea() {
        if (!editModeCheck(this.editMode)) return;

        const form = this.configFormGroup.value;
        let fillArea = !form.fillArea;

        this.configFormGroup.patchValue({
            fillArea
        });
    }

    resetPosition() {
        if (!editModeCheck(this.editMode)) return;

        this.configFormGroup.patchValue({
            focalX: 0.5,
            focalY: 0.5,
            zoom: 1,
        });
        this._applyCropStyle();
    }

    onImageDoubleClick(event: MouseEvent) {
        if (!editModeCheck(this.editMode)) return;
        if (!this.configFormGroup.value.fillArea) return;

        event.stopPropagation();
        event.preventDefault();
        this.repositioning = true;
        this._setGridsterItemOverflow(true);
    }

    exitReposition(event?: MouseEvent) {
        if (event) {
            event.stopPropagation();
            event.preventDefault();
        }
        this.repositioning = false;
        this._setGridsterItemOverflow(false);
    }

    onPointerDown(event: PointerEvent) {
        if (!this.repositioning) return;
        event.stopPropagation();
        event.preventDefault();

        this._dragging = true;
        this._dragStartX = event.clientX;
        this._dragStartY = event.clientY;
        this._dragStartFocalX = this.configFormGroup.value.focalX ?? 0.5;
        this._dragStartFocalY = this.configFormGroup.value.focalY ?? 0.5;
    }

    // angular-gridster2 initiates its own drag-to-move on 'mousedown'/'touchstart' (confirmed in its
    // source, gridsterDraggable.service.js), NOT 'pointerdown' — stopping propagation on the pointer
    // event alone does not stop these separately-dispatched events from also reaching gridster, so the
    // component's own grid frame was still dragging/snapping underneath the focal-point pan. This
    // suppresses gridster's listeners specifically while repositioning is active.
    suppressGridsterDrag(event: MouseEvent | TouchEvent) {
        if (!this.repositioning) return;
        event.stopPropagation();
        event.preventDefault();
    }

    @HostListener('document:pointermove', ['$event'])
    onDocumentPointerMove(event: PointerEvent) {
        if (!this._dragging) return;

        const crop = this._computeCrop();
        if (!crop) return;

        const deltaX = event.clientX - this._dragStartX;
        const deltaY = event.clientY - this._dragStartY;

        // Dragging the image right should reveal more of its left side (the anchor point moves
        // opposite the drag direction) — hence the minus sign. Divides by the TRUE computed overflow
        // (real natural image size vs. frame, at the current zoom) rather than an approximation, so a
        // 1px mouse move always maps to exactly 1px of visual image movement. If an axis has zero
        // overflow (e.g. the image's native aspect ratio already exactly fills that axis at this zoom),
        // that axis is correctly a no-op instead of dividing by zero.
        const focalX = crop.overflowX > 0 ? this._dragStartFocalX - (deltaX / crop.overflowX) : this._dragStartFocalX;
        const focalY = crop.overflowY > 0 ? this._dragStartFocalY - (deltaY / crop.overflowY) : this._dragStartFocalY;

        this.configFormGroup.patchValue({
            focalX: Math.min(1, Math.max(0, focalX)),
            focalY: Math.min(1, Math.max(0, focalY)),
        });

        this._applyCropStyle();
    }

    @HostListener('document:pointerup')
    onDocumentPointerUp() {
        // A drag's mouseup is followed by a synthetic 'click' event at the release point — which is
        // very often OUTSIDE the small crisp frame (the whole point of dragging is to move around a
        // wider area, including the greyed spillover zone) — so onDocumentClick needs to know a drag
        // JUST ended and ignore that one click, rather than treating it as "clicked off the image".
        if (this._dragging) {
            this._justFinishedDrag = true;
        }
        this._dragging = false;
    }

    @HostListener('document:keydown.escape')
    onEscapeKey() {
        if (!this.repositioning) return;
        this.exitReposition();
    }

    // Clicking anywhere off the image while repositioning should exit — but a click ON the image
    // itself must not (that's how dragging/zooming happens). Bound on 'document' rather than the host
    // template so it also catches clicks on unrelated page content, not just this component's own
    // wrapper. Safe against the double-click that ENTERS reposition mode: browsers dispatch click,
    // click, dblclick in sequence, and 'repositioning' only flips true inside the dblclick handler —
    // by then both constituent clicks have already fired and been ignored (repositioning was still
    // false), so this listener can't immediately undo the same gesture that just enabled it.
    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent) {
        if (!this.repositioning) return;

        if (this._justFinishedDrag) {
            this._justFinishedDrag = false;
            return;
        }

        const target = event.target as HTMLElement;
        const frameEl = this._getFrameEl();
        if (frameEl && frameEl.contains(target)) return;

        // The dimmed .ghost-context spillover is conceptually part of the same editable image area
        // (it's still the same photo, just showing what the current crop would cut off), but it's
        // pointer-events:none, so a click there never lands on it as event.target — it hits whatever's
        // behind it (the page background) instead. Must be checked geometrically (against its
        // bounding rect) rather than via DOM containment. Note: the SPILLOVER SIZE lives on the ghost's
        // own <img> (that's what's actually oversized once zoomed) — .ghost-context itself is just the
        // small wrapper sized to match the frame, so checking that wrapper's own rect would wrongly
        // exclude the very spillover area this check exists for.
        const ghostImg = this._el.nativeElement.querySelector('.ghost-context img') as HTMLElement | null;
        if (ghostImg) {
            const r = ghostImg.getBoundingClientRect();
            if (event.clientX >= r.left && event.clientX <= r.right && event.clientY >= r.top && event.clientY <= r.bottom) {
                return;
            }
        }

        this.exitReposition();
    }

    onWheel(event: WheelEvent) {
        if (!this.repositioning) return;
        event.preventDefault();

        const frameEl = this._getFrameEl();
        const cropBefore = this._computeCrop();
        if (!frameEl || !cropBefore) return;

        const rect = frameEl.getBoundingClientRect();
        const cursorX = event.clientX - rect.left;
        const cursorY = event.clientY - rect.top;

        const focalXBefore = typeof this.configFormGroup.value.focalX === 'number' ? this.configFormGroup.value.focalX : 0.5;
        const focalYBefore = typeof this.configFormGroup.value.focalY === 'number' ? this.configFormGroup.value.focalY : 0.5;

        // Express the image-space point currently under the cursor as a fraction of the rendered
        // image (0-1 in each axis), using the crop BEFORE the zoom change.
        const leftBefore = -cropBefore.overflowX * focalXBefore;
        const topBefore = -cropBefore.overflowY * focalYBefore;
        const imgFracX = cropBefore.renderedW > 0 ? (cursorX - leftBefore) / cropBefore.renderedW : 0.5;
        const imgFracY = cropBefore.renderedH > 0 ? (cursorY - topBefore) / cropBefore.renderedH : 0.5;

        const current = typeof this.configFormGroup.value.zoom === 'number' ? this.configFormGroup.value.zoom : 1;
        // Scroll up (negative deltaY) zooms in; sensitivity tuned so a typical scroll notch moves
        // roughly 0.1 of the 1-3 range per tick — coarse but predictable, refinable later.
        const next = Math.min(3, Math.max(1, current - (event.deltaY * 0.001)));

        this.configFormGroup.patchValue({ zoom: next });

        const cropAfter = this._computeCrop();
        if (!cropAfter) {
            this._applyCropStyle();
            return;
        }

        // Re-solve focalX/focalY so the SAME image-space fraction still sits under the cursor at the
        // new zoom — this is what makes the zoom feel anchored to the mouse instead of the frame centre.
        const newLeft = cursorX - imgFracX * cropAfter.renderedW;
        const newTop = cursorY - imgFracY * cropAfter.renderedH;
        const focalX = cropAfter.overflowX > 0 ? Math.min(1, Math.max(0, -newLeft / cropAfter.overflowX)) : focalXBefore;
        const focalY = cropAfter.overflowY > 0 ? Math.min(1, Math.max(0, -newTop / cropAfter.overflowY)) : focalYBefore;

        this.configFormGroup.patchValue({ focalX, focalY });
        this._applyCropStyle();
    }

    edit() {
        if (!editModeCheck(this.editMode)) return;

        // imageControl.value takes two shapes depending on its source: {key, ...} right after a fresh local
        // selection closes this dialog (report-image-edit-form.dialog.ts's attempt()), {dataKey, ...} once
        // report-component.view.ts's componentState$ round-trips and re-applies the server-persisted shape.
        // Reading only `dataKey` meant reopening the dialog immediately after selecting (before that round
        // trip lands) always looked unselected — imageKey was undefined, so reset() found no match.
        const data: ReportImageEditFormDialogData = {
            imageKey: this.imageControl.value?.dataKey ?? this.imageControl.value?.key,
            reportKey: this.reportKey,
            ownerOrgKey: this.ownerOrgKey,
            context: this.imageControl.value?.context,
        }

        this._dialog.open(ReportImageEditFormDialog, {
            data,
            panelClass: 'reporting-theme',
            width: '720px',
            maxWidth: '95vw',
            autoFocus: false,
        }).afterClosed()
        .subscribe((result) => {
            if (!result?.status) return;

            this.imageControl.setValue(result?.data);
            this.imageControl.markAsDirty();
        })
    }

    remove() {
        this._dialog.confirm(
            'Delete Component',
            `Are you sure you want to delete this component?`,
            'Delete',
            'Cancel',
            true
        )
        .afterClosed()
        .subscribe(result => {
            if (!result) return;

            this.delete.emit(true);
        })
    }

    makeDuplicate() {
        this.duplicate.emit(ReportComponentType.IMAGE);
    }
}